import ArrowRightIcon from "~icons/ph/arrow-right";
import { Link } from "@tanstack/react-router";
import { useEffect } from "react";
import { translate } from "../../shared/translations";
import { Backdrop, Wave } from "../ui/components";
import { setLocale, useLocale } from "../i18n";

const privacy = [
  [
    "بيانات الحساب والمحادثات",
    "تُحفظ بيانات الاسم واسم المستخدم والنبذة، وطلبات الإضافة والصداقات والرسائل وسجل المكالمات، على خوادم التطبيق لتقديم الخدمة.",
  ],
  [
    "الصوت والفيديو والشاشة",
    "مكالماتك مشفّرة بينك وبين الطرف الآخر. لا تسجّل رنّة الصوت أو الفيديو أو محتوى الشاشة ولا تخزّنه. أنت تتحكّم في الميكروفون والكاميرا ومشاركة الشاشة، ويمكنك إيقافها في أي وقت.",
  ],
  [
    "المتصفح والجلسة",
    "تستخدم رنّة ملف تعريف ارتباط للجلسة، وتحفظ اختيار اللغة وتفضيل تذكير الاستعادة في المتصفح. رمز الاستعادة لا يُحفظ تلقائيًا في تخزين المتصفح. مع مفتاح المرور تحفظ رنّة مفتاحًا عامًا فقط، ولا تصل إليها بصمتك أو وجهك أو قفل شاشتك. عندما يطلب جهاز جديد الدخول، تحفظ رنّة نوعه ومدينته التقريبية مع الطلب الذي يدوم خمس دقائق لتعرضهما عليك قبل الموافقة، ثم تحذف الطلبات المنتهية. وتحفظ النوع والمدينة، دون عنوان IP، مع كل جلسة ما دامت قائمة، ولا تعرضهما إلا على أجهزة حسابك.",
  ],
  [
    "من يرى نشاطك",
    "يرى أصدقاؤك المقبولون حالة اتصالك وآخر ظهور لك وهل قرأت رسائلهم وفق إعداداتك. يمكنك إخفاء آخر ظهور وحالة القراءة أو اختيار الظهور كغير متصل من الإعدادات.",
  ],
] as const;
const terms = [
  [
    "التواصل باختيار الطرفين",
    "تتاح الرسائل والمكالمات بعد قبول طلب الإضافة. احترم رغبة الآخرين، ولا تستخدم التطبيق للمضايقة أو انتحال الشخصية أو إرسال محتوى غير قانوني.",
  ],
  [
    "حسابك وطرق العودة إليه",
    "يمكنك استخدام اسم مستعار. أضف مفتاح مرور أو احتفظ برمز الاستعادة في مكان خاص إذا أردت العودة إلى الحساب من جهاز آخر أو بعد مسح بيانات المتصفح. من يمتلك الرمز يستطيع الدخول إلى الحساب.",
  ],
  [
    "الأذونات والتوفر",
    "تحتاج المكالمات إلى متصفح متوافق واتصال بالإنترنت وأذونات الميكروفون أو الكاميرا بحسب اختيارك. تعتمد مشاركة الشاشة على دعم المتصفح، وقد تتعطل الخدمة أو ينقطع الاتصال.",
  ],
  [
    "إدارة من تتواصل معهم",
    "يمكنك إزالة صديق أو حظر حساب من خيارات المحادثة، وإدارة الحسابات المحظورة من الإعدادات. إزالة الصداقة توقف الرسائل والمكالمات بين الطرفين.",
  ],
] as const;

export function InformationPage() {
  const locale = useLocale();
  // The compiler needs the locale as a dependency of each translation.
  const t = (source: string) => translate(source, locale);
  const path = location.pathname.replace(/\/$/, "");
  const isPrivacy = path === "/privacy";
  const known = isPrivacy || path === "/terms";
  const title = known
    ? t(isPrivacy ? "الخصوصية" : "شروط الاستخدام")
    : t("هذه الصفحة غير موجودة");
  const pageTitle = `${title} · ${t("رنّة")}`;
  useEffect(() => {
    document.title = pageTitle;
    const robots = document.createElement("meta");
    robots.name = "robots";
    robots.content = "noindex";
    document.head.appendChild(robots);
    return () => {
      robots.parentNode?.removeChild(robots);
    };
  }, [pageTitle]);
  return (
    <div className="ranna-app information-page">
      <Backdrop paused />
      <a className="skip-link" href="#main-content">
        {t("تجاوز إلى المحتوى")}
      </a>
      <header className="site-header">
        <Link
          to="/"
          search={{ lang: locale }}
          className="brand"
          aria-label={t("رنّة — الرئيسية")}
        >
          <Wave />
          <span>{t("رنّة")}</span>
        </Link>
        <button
          className="language-button"
          lang={locale === "ar" ? "en" : "ar"}
          aria-label={locale === "ar" ? "Switch to English" : "التبديل للعربية"}
          onClick={() => setLocale(locale === "ar" ? "en" : "ar")}
        >
          {locale === "ar" ? "EN" : "عربي"}
        </button>
      </header>
      <main className="information-content" id="main-content" tabIndex={-1}>
        <p className="eyebrow">{known ? t("عن رنّة") : "404"}</p>
        <h1>{title}</h1>
        {known ? (
          <div className="information-sections">
            {(isPrivacy ? privacy : terms).map(([heading, body]) => (
              <section key={heading}>
                <h2>{t(heading)}</h2>
                <p>{t(body)}</p>
              </section>
            ))}
          </div>
        ) : (
          <p>{t("قد يكون الرابط قد تغيّر. يمكنك العودة للبحث عن أصدقائك.")}</p>
        )}
        <Link className="button secondary" to="/" search={{ lang: locale }}>
          <ArrowRightIcon
            className="directional"
            width={18}
            height={18}
            aria-hidden="true"
          />
          {t("العودة")}
        </Link>
      </main>
    </div>
  );
}
